<script setup>
    import { ref, onMounted, watch, computed } from 'vue'
    import { Link, router, usePage } from '@inertiajs/vue3'
    import MarketingFlyout from '@/Components/scene/MarketingFly.vue'
    import posthog from 'posthog-js'
    import { useTagsStore } from '@/store/tagsStore'
    import AccessModal from '@/Components/Auth/AccessModal.vue'
    import SubscriptionModal from '@/Components/navigation/SubscriptionModal.vue'
    import TokenPurchaseModal from '@/Components/navigation/TokenPurchaseModal.vue'
    import { useAuthReloader } from '@/composables/useAuthReloader'

    // Only trigger auth reloader if user is logged in
    const page = usePage()
    if (page.props.auth?.user) {
        useAuthReloader()
    }
    const user = computed(() => page.props.auth?.user ?? null)

    const emit = defineEmits(['openNoLiveModal'])
    const liveShowsEnabled = ref(import.meta.env.VITE_LIVE_SHOWS_ENABLED)

    const handleLiveClick = e => {
        if (!props?.creator?.allow_live || false) {
            e.preventDefault()
            emit('openNoLiveModal')
        }
    }

    const tagsStore = useTagsStore()

    const resetAndNavigate = () => {
        // Use router.get() for faster, more reliable navigation
        // Avoid route() helper which can cause async delays and canceled requests
        const exploreUrl = '/explore'

        // Prevent navigation if already on explore page
        if (window.location.pathname === exploreUrl) {
            return
        }

        router.get(
            exploreUrl,
            {},
            {
                preserveState: false,
                preserveScroll: false,
                only: [],
                onError: errors => {
                    console.error('Navigation error:', errors)
                },
            },
        )
    }

    let pathName = window.location.pathname
    const showMarketingFlyout = ref(false)
    const openMarketingFlyout = () => {
        showMarketingFlyout.value = true
    }

    const goMessagePage = () => {
        router.visit(route('message'))
    }

    const closeMarketingFlyout = () => {
        showMarketingFlyout.value = false
    }
    const profile = computed(() => user.value)
    const props = page.props

    const { isLoggedIn, avatarImage, isCreator, pathNameProp, reff } = defineProps({
        isLoggedIn: {
            type: Boolean,
            default: false,
        },

        pathNameProp: {
            type: String,
            default: undefined,
        },

        reff: {
            type: String,
            default: undefined,
        },
    })

    const backreff = page.props.backref.includes('message')

    import { initFlowbite } from 'flowbite'

    onMounted(() => {
        pathNameProp
        initFlowbite
    })

    const isAccessModalOpen = ref(false)
    const showRegisterFirst = ref(false)
    const isSubscriptionModalOpen = ref(false)
    const isTokenPurchaseModalOpen = ref(false)

    const openAccessModal = (registerFirst = false) => {
        showRegisterFirst.value = registerFirst
        isAccessModalOpen.value = true
    }

    const closeAccessModal = () => {
        isAccessModalOpen.value = false
    }

    const openSubscriptionModal = () => {
        isSubscriptionModalOpen.value = true
    }

    const openTokenPurchaseModal = () => {
        isTokenPurchaseModalOpen.value = true
    }

    const closeSubscriptionModal = () => {
        isSubscriptionModalOpen.value = false
    }

    const closeTokenPurchaseModal = () => {
        isTokenPurchaseModalOpen.value = false
    }

    const handleClickOnJoinFree = () => {
        posthog.capture('clicked_join_free', {
            path: pathName,
        })

        if (pathNameProp === '/message') {
            fathom.trackEvent('exn_lander_join_nav_click')
        }

        openAccessModal(true)
    }

    watch(
        () => user.value,
        user => {
            if (user) {
                closeAccessModal()
            }
        },
    )
    const aiTokens = computed(() => user.value?.balance_tokens)
</script>
<template>
    <div
        class="hidden h-[67px] min-w-[363px] align-top sm:flex sm:items-center sm:justify-end lg:justify-evenly">
        <div
            class="relative flex gap-[25px] lg:min-w-[340px] lg:gap-[25px] xl:min-w-[360px] xl:gap-[38px]"
            v-if="isLoggedIn">
            <div @click="resetAndNavigate" title="Explore" alt="Explore" class="flex items-center">
                <font-awesome-icon
                    title="Explore"
                    alt="Explore"
                    :icon="['fas', 'magnifying-glass']"
                    class="mx-auto h-[27px] w-[27px] cursor-pointer text-iconWhite hover:text-gray-400 dark:text-white dark:hover:text-mintgreen" />
            </div>
            <Link
                :href="route('notifications', { backref: pathName })"
                title="notifications"
                alt="notifications"
                class="relative flex items-center">
                <font-awesome-icon
                    title="notifications"
                    alt="notifications"
                    :icon="['fas', 'bell']"
                    class="mx-auto h-[30px] w-[26px] cursor-pointer text-iconWhite hover:text-gray-400 dark:text-white dark:hover:text-mintgreen" />
                <div
                    v-if="profile?.notifications_count > 0"
                    class="absolute left-[15px] top-[4px] h-[19px] w-[19px] rounded-full border-[2px] border-alertred bg-alertred dark:border-alertred">
                    <span
                        class="absolute left-[1px] top-0 h-[16px] w-[15px] text-center text-[10px] font-medium leading-[16px] text-white"
                        >{{
                            profile?.notifications_count > 9 ? '+9' : profile?.notifications_count
                        }}</span
                    >
                </div>
            </Link>

            <a
                v-if="isLoggedIn"
                title="Messages"
                :href="route('chatify', { origin: pathName })"
                class="relative flex items-center">
                <font-awesome-icon
                    :icon="['fass', 'comment']"
                    class="mx-auto h-[30px] w-[30px] cursor-pointer text-iconWhite dark:text-white dark:hover:text-mintgreen" />
                <div
                    v-if="profile?.conversations_count > 0"
                    class="absolute left-[15px] top-[4px] h-[19px] w-[19px] rounded-full border-[2px] border-alertred bg-alertred dark:border-alertred">
                    <span
                        class="absolute left-[1px] top-0 h-[16px] w-[15px] text-center text-[10px] font-medium leading-[16px] text-white"
                        >{{
                            profile?.conversations_count > 9 ? '+9' : profile?.conversations_count
                        }}</span
                    >
                </div>
            </a>
            <Link
                href="/message"
                v-if="!isLoggedIn"
                title="Messages"
                alt="Messages"
                class="flex items-center">
                <font-awesome-icon
                    :icon="['fass', 'comment']"
                    class="mx-auto h-[30px] w-[30px] cursor-pointer text-iconWhite dark:text-white dark:hover:text-mintgreen" />
            </Link>

            <Link
                :href="route('profile.wallet', { backref: pathName })"
                class="relative flex items-center"
                v-if="!props.creator.isCreator">
                <img
                    src="/images/fyf-token.png"
                    alt="Fantasy Credits"
                    class="mx-auto h-[30px] w-[30px] cursor-pointer hover:opacity-70 dark:hover:opacity-70" />
                <span v-if="aiTokens !== undefined" class="ml-2 text-[14px] font-medium text-white">
                    {{ aiTokens }}
                </span>
            </Link>
            <Link
                :href="route('creator.start', { backref: pathName })"
                class="flex items-center"
                v-else>
                <font-awesome-icon
                    :icon="['fas', 'circle-arrow-up']"
                    class="mx-auto h-[30px] w-[30px] cursor-pointer text-iconWhite hover:text-gray-400 dark:text-white dark:hover:text-mintgreen" />
            </Link>
            <a
                @click="handleLiveClick"
                :href="route('creator.live_show', props.creator.profile_url)"
                v-if="props.creator.isCreator && liveShowsEnabled === 'true'"
                class="flex items-center">
                <font-awesome-icon
                    :icon="['far', 'video']"
                    class="mx-auto h-[30px] w-[30px] cursor-pointer text-iconWhite hover:text-gray-400 dark:text-white dark:hover:text-mintgreen" />
            </a>
            <!-- Profile Link -->
            <div class="relative flex" v-if="isLoggedIn">
                <Link
                    :href="
                        route('profile.show', props.auth.user.profile_url, { backref: pathName })
                    "
                    class="inline-flex items-center rounded-md">
                    <button
                        type="button"
                        class="inline-flex h-[30px] w-[30px] items-center rounded-md text-sm font-medium leading-4 text-gray-500 transition duration-150 ease-in-out hover:text-gray-700 focus:outline-none">
                        <img
                            v-if="props.avatarImage"
                            :src="props.avatarImage"
                            class="h-[30px] w-[30px] rounded-full"
                            loading="lazy" />
                        <div v-else>
                            <font-awesome-icon
                                :icon="['fa-solid', 'circle-user']"
                                class="mx-auto h-7 w-7 cursor-pointer rounded-full text-white hover:text-gray-400 dark:hover:text-mintgreen" />
                        </div>
                    </button>
                </Link>
            </div>
            <!-- drawer init and toggle -->
            <div class="flex items-center">
                <button
                    class="mx-auto h-[36px] w-[30px] cursor-pointer text-2xl text-iconWhite hover:text-mintgreen dark:text-white dark:hover:text-mintgreen"
                    type="button"
                    data-drawer-target="right-navigation"
                    data-drawer-show="right-navigation"
                    data-drawer-backdrop="true"
                    aria-controls="right-navigation"
                    data-drawer-placement="right">
                    <font-awesome-icon
                        class="h-[36px] w-[9px]"
                        :icon="['fas', 'ellipsis-vertical']" />
                </button>
            </div>
        </div>

        <div
            class="relative flex h-[67px] gap-[25px] lg:gap-[25px] xl:right-[0] xl:gap-[28px]"
            v-if="!isLoggedIn">
            <div
                @click="resetAndNavigate"
                class="flex items-center"
                alt="Explore"
                title="Explore"
                v-if="!props.hideHeaderSearch">
                <font-awesome-icon
                    alt="Explore icon"
                    title="Explore"
                    :icon="['fas', 'magnifying-glass']"
                    class="mx-auto h-[27px] w-[27px] cursor-pointer text-iconWhite hover:text-mintgreen dark:text-white" />
            </div>

            <a href="/message" class="flex items-center" title="Messages" alt="Messages">
                <font-awesome-icon
                    alt="message icon"
                    :icon="['fass', 'comment']"
                    class="mx-auto h-[30px] w-[30px] cursor-pointer text-iconWhite hover:text-mintgreen dark:text-white" />
            </a>

            <span class="inline-flex rounded-md">
                <button
                    alt="Profile"
                    title="Profile"
                    type="button"
                    class="inline-flex items-center rounded-md border border-transparent py-2 text-sm font-medium leading-4 text-gray-500 transition duration-150 ease-in-out hover:text-gray-700 focus:outline-none"
                    @click="openAccessModal(false)">
                    <div>
                        <font-awesome-icon
                            :icon="['fas', 'circle-user']"
                            class="mx-auto h-[30px] w-[30px] cursor-pointer rounded-full text-white hover:text-mintgreen" />
                        <span
                            class="absolute left-0 top-[10px] w-full text-center font-semibold capitalize leading-[28px] text-white">
                        </span>
                    </div>
                </button>
            </span>

            <template v-if="backreff">
                <div
                    v-if="!isLoggedIn"
                    @click="handleClickOnJoinFree"
                    class="flex cursor-pointer items-center">
                    <span
                        alt="Join Free"
                        title="Join Free"
                        class="hidden w-[130px] cursor-pointer rounded-[10px] bg-mintgreen text-center text-[1rem] font-medium leading-[34px] tracking-[0.03rem] dark:text-black dark:hover:bg-hovermintgreen sm:block">
                        Join Free
                    </span>
                </div>
            </template>

            <template v-else>
                <div
                    v-if="!isLoggedIn"
                    @click="handleClickOnJoinFree"
                    class="flex cursor-pointer items-center">
                    <span
                        alt="Join Free"
                        title="Join Free"
                        class="hidden w-[130px] cursor-pointer rounded-[10px] bg-mintgreen text-center text-[1rem] font-medium leading-[34px] tracking-[0.03rem] dark:text-black dark:hover:bg-hovermintgreen sm:block">
                        Join Free
                    </span>
                </div>
            </template>

            <!-- drawer init and toggle -->
            <div class="flex items-center">
                <button
                    class="mx-auto h-[36px] w-[30px] cursor-pointer text-2xl text-iconWhite hover:text-mintgreen dark:text-white dark:hover:text-mintgreen"
                    data-drawer-target="right-navigation"
                    data-drawer-show="right-navigation"
                    data-drawer-backdrop="true"
                    alt="Open Menu"
                    title="Open Menu"
                    aria-controls="right-navigation"
                    data-drawer-placement="right">
                    <font-awesome-icon
                        class="h-[36px] w-[9px]"
                        :icon="['fas', 'ellipsis-vertical']" />
                </button>
            </div>
        </div>
    </div>
    <div class="flex items-center sm:hidden">
        <div class="relative" v-if="isLoggedIn">
            <Link :href="route('notifications', { backref: pathName })" alt="notifications">
                <font-awesome-icon
                    alt="notifications icon"
                    :icon="['fas', 'bell']"
                    class="block w-6 pt-1 dark:text-white sm:hidden" />
                <span
                    class="dark:border-white-100 absolute left-3 top-0 h-2.5 w-2.5 rounded-full border-white bg-alertred"></span>
            </Link>
        </div>
        <div
            v-if="!isLoggedIn"
            @click="openMarketingFlyout"
            alt="Join Free"
            title="Join Free"
            class="mobile-join cursor-pointer rounded-[10px] bg-mintgreen px-[16px] py-[6px] text-[14px] font-medium leading-[17px]">
            JOIN FREE
        </div>

        <!-- Hamburger -->
        <div class="-mr-2 flex items-center sm:hidden">
            <button
                alt="Open Menu"
                data-drawer-target="right-navigation"
                data-drawer-show="right-navigation"
                data-drawer-backdrop="true"
                aria-controls="right-navigation"
                data-drawer-placement="right"
                class="inline-flex items-center justify-center rounded-md p-3 text-2xl text-gray-300 transition duration-150 ease-in-out hover:text-mintgreen focus:text-mintgreen focus:outline-none">
                <font-awesome-icon :icon="['fas', 'ellipsis-vertical']" />
            </button>
        </div>
    </div>
    <MarketingFlyout
        :show="showMarketingFlyout"
        @close="showMarketingFlyout = false"
        :hideScroll="false" />
    <AccessModal
        :show="isAccessModalOpen"
        @close="closeAccessModal"
        :show-register-first="showRegisterFirst"
        :can-reset-password="props?.canResetPassword"
        :status="props?.status" />
    <SubscriptionModal :show="isSubscriptionModalOpen" @close="closeSubscriptionModal" />
    <TokenPurchaseModal :show="isTokenPurchaseModalOpen" @close="closeTokenPurchaseModal" />
</template>
<style scoped>
    .custom-tooltip {
        position: relative;
        cursor: pointer;
    }

    .custom-tooltip::before {
        content: attr(title);
        position: absolute;
        background-color: gray;
        color: white;
        padding: 1px 3px;
        border-radius: 3px;
        font-size: 10px;
        white-space: nowrap;
        opacity: 0;
        visibility: hidden;
        transition: opacity 0.2s, visibility 0.2s;
        top: 105%;
        left: 50%;
        transform: translateX(-50%);
    }

    .custom-tooltip:hover::before {
        opacity: 0.9;
        visibility: visible;
    }
</style>
